/* eslint-disable jsx-a11y/role-supports-aria-props */
import { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { isFieldLocked } from '../../../utils/fieldUtils';
import { InputAdornment, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { abortController } from '../../../utils/abortController';
import useUser from '../../../hooks/useUser';
import useSessionExpire from '../../../hooks/useSessionExpire';
import axios from 'axios';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import Button from '@mui/material/Button';
import VirtualizedAutocomplete from '../../../components/Autocomplete';

const BuyingPaymentTerms = forwardRef(({
    isWritable = false,
    isListDataLoading = false,
    paymentTermTypes = [],
    listData = {},
    selectedPFI = null,
    selectedBusinessUnit = null,
    isPeriodLocked = false,
    autoPopulatePFI = null,
    lockedFields = [],
    expandedFormSections = [],
    resetKey = 0,
    dealHeaderFormData = {},
    onSblcLabelChange = () => { },
    setExpandedFormSections = () => { },
    setIsUpdated = () => { },
    setShowAlert = () => { },
    setAlertMessage = () => { },
    setAlertType = () => { },
    logOut = () => { },
}, ref) => {

    const currentUser = useUser();
    const triggerSessionExpire = useSessionExpire();
    const formRef = useRef(null);
    const isFirstSblcFetch = useRef(true);

    const formDataSchema = useMemo(() => ({
        id: null,
        payment_term_type_id: null,
        advance_percentage: null,
        remaining_balance_percentage: null,
        tenor_type: null,
        lc_type: null,
        usance_days: null,
        sblc_id: null,
        advance_amount: null,
        from_date_field: null,
    }), []);

    const [formData, setFormData] = useState(structuredClone(formDataSchema));
    const [initialFormData, setInitialFormData] = useState(structuredClone(formDataSchema));
    const [isExisting, setIsExisting] = useState(false);
    const [isDetailLoading, setIsDetailLoading] = useState(false);

    // Internal SBLC list — fetched independently using vendor_type = 'supplier'
    const [sblcList, setSblcList] = useState([]);
    const [isSblcListLoading, setIsSblcListLoading] = useState(false);

    const selectedType = paymentTermTypes.find(t => t.id === formData.payment_term_type_id) || null;
    const ptID = (selectedType?.id || null);

    const isMixedPayment = ptID === 2;
    const isLC = ptID === 3;
    const isSBLC = ptID === 4;
    const isOpenAccount = ptID === 5;
    const isCollection = ptID === 6;

    const tenorType = (formData.tenor_type || "").toUpperCase();
    const lcType = (formData.lc_type || "").toUpperCase();

    const mixedTenorOptions = useMemo(() => ["LC", "Open Account"], []);
    const mixedLCTypeOptions = useMemo(() => ["Sight", "Usance"], []);
    const lcTenorOptions = useMemo(() => ["Sight", "Usance", "Mixed"], []);
    const collectionTypeOptions = useMemo(() => ["D/P", "D/A"], []);

    // MODAL VARIABLES
    const [showSblcWarningModal, setShowSblcWarningModal] = useState(false);
    const [sblcWarningMessage, setSblcWarningMessage] = useState("");
    // Warning for invalid SBLC (isValid === false)
    const [showSblcInvalidModal, setShowSblcInvalidModal] = useState(false);

    const allFromDateOptions = useMemo(() => [
        { id: "bl_date", value: "BL Date" },
        { id: "copy_docs", value: "Copy Docs" },
        { id: "acceptance", value: "Acceptance" },
    ], []);

    const fromDateOptionsWithCopyDocs = useMemo(() => allFromDateOptions, [allFromDateOptions]);
    const fromDateOptionsNoCopyDocs = useMemo(() =>
        allFromDateOptions.filter(o => o.id !== "copy_docs"), [allFromDateOptions]);

    const autoRemainingPct = useMemo(() => {
        const val = parseFloat(formData.advance_percentage);
        return (!isNaN(val) && val >= 0 && val <= 100) ? (100 - val).toFixed(2) : "";
    }, [formData.advance_percentage]);

    // ── Calculate buying deal value from products ─────────────────────────────
    const buyingDealValue = useMemo(() => {
        const products = dealHeaderFormData?.products;
        if (!Array.isArray(products) || products.length === 0) return 0;
        return products.reduce((sum, p) => {
            const price = parseFloat(p?.Buying_Price) || 0;
            const qty = parseFloat(p?.Quantity) || 0;
            return sum + price * qty;
        }, 0);
    }, [dealHeaderFormData?.products]);

    // ── Warn when deal value exceeds selected SBLC limit (no deselect, no disable) ───────
    useEffect(() => {
        if (!isSBLC) return;
        if (!formData.sblc_id) return;

        const sblcIdValue = formData.sblc_id?.id ?? formData.sblc_id;
        const selectedSblc = sblcList.find(s => s.id === sblcIdValue);
        if (!selectedSblc) return;

        const limit = parseFloat(selectedSblc.limit);
        if (!isNaN(limit) && buyingDealValue > limit) {
            const excess = (buyingDealValue - limit).toLocaleString();
            setSblcWarningMessage(
                `The buying deal value (${buyingDealValue.toLocaleString()}) has exceeded the selected SBLC limit (${limit.toLocaleString()}). The SBLC balance has gone into negative by ${excess}. Please review your selection.`
            );
            setShowSblcWarningModal(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyingDealValue]);


    const sblcOptionsWithDisabled = useMemo(() =>
        sblcList.map(option => ({
            ...option,
            _disabled: false,
        })),
        [sblcList]
    );

    useEffect(() => {
        isFirstSblcFetch.current = true;
    }, [dealHeaderFormData?.Deal_PFI_Id]);

    useEffect(() => {
        const fetchSblcList = async () => {
            if (isFirstSblcFetch.current) {
                isFirstSblcFetch.current = false;
            } else {
                setFormData(prev => ({ ...prev, sblc_id: null })); // only reset on actual change
            }
            setIsSblcListLoading(true);
            try {
                const response = await axios({
                    method: "post",
                    url: "/api/v2/GetSBLCList",
                    data: {
                        sblc_leg: "Buying",
                        vendor_id: dealHeaderFormData?.Seller_Id?.id ?? null,
                    },
                    signal: abortController.signal,
                });
                if (response.status === 200) {
                    setSblcList(response.data?.sblc_list || []);

                }
            } catch (error) {
                if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
                console.error("GetSBLCList (Buying) error:", error);
            } finally {
                setIsSblcListLoading(false);
            }
        };

        if (dealHeaderFormData?.Seller_Id?.id) {
            fetchSblcList();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dealHeaderFormData?.Seller_Id?.id]);

    /* ── FETCH PAYMENT TERM DETAIL ───────────────────────────────────────────── */
    useEffect(() => {
        const pfiToFetch = autoPopulatePFI || selectedPFI;

        if (!pfiToFetch) {
            setFormData(structuredClone(formDataSchema));
            setInitialFormData(structuredClone(formDataSchema));
            setIsExisting(false);
            return;
        }

        if (resetKey > 0 && !autoPopulatePFI) {
            setFormData(structuredClone(formDataSchema));
            setInitialFormData(structuredClone(formDataSchema));
            setIsExisting(false);
            return;
        }

        const fetchDetail = async () => {
            setIsDetailLoading(true);
            try {
                const response = await axios({
                    method: "post",
                    url: "/api/GetPaymentTermDetail",
                    data: {
                        Business_Unit_Id: selectedBusinessUnit,
                        deal_pfi_id: pfiToFetch,
                        payment_term_leg: "Buying",
                        user_id: currentUser?.id,
                    },
                    signal: abortController.signal,
                });

                const { status, data } = response;

                if (status === 200 && data?.length > 0) {
                    const record = data[0];
                    const isAutoPopulate = Boolean(autoPopulatePFI);
                    const loaded = {
                        id: isAutoPopulate ? null : record.id,
                        payment_term_type_id: record.payment_term_type_id,
                        advance_percentage: record.advance_percentage,
                        remaining_balance_percentage: record.remaining_balance_percentage,
                        tenor_type: record.tenor_type,
                        lc_type: record.lc_type,
                        usance_days: record.usance_days,
                        sblc_id: record.sblc_id,
                        advance_amount: record.advance_amount,
                        from_date_field: allFromDateOptions.find(o => o.id === record.from_date_field) ?? null,
                    };
                    setFormData(structuredClone(loaded));
                    setInitialFormData(structuredClone(loaded));
                    setIsExisting(!isAutoPopulate);
                }
            } catch (error) {
                if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
                console.error("GetPaymentTermDetail (Buying) error:", error);
                handleApiError(error);
            } finally {
                setIsDetailLoading(false);
            }
        };

        fetchDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPFI, autoPopulatePFI, resetKey]);

    useEffect(() => {
        const updated = JSON.stringify(formData) !== JSON.stringify(initialFormData);
        setIsUpdated(updated);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData, initialFormData]);

    const resetSubFields = useCallback(() => {
        setFormData(prev => ({
            ...prev,
            advance_percentage: null,
            remaining_balance_percentage: null,
            tenor_type: null,
            lc_type: null,
            usance_days: null,
            sblc_id: null,
            advance_amount: null,
            from_date_field: null,
        }));
    }, []);

    const handleAccordionChange = (section) => (_, isExpanded) => {
        setExpandedFormSections(
            isExpanded
                ? [...expandedFormSections, section]
                : expandedFormSections.filter(id => id !== section)
        );
    };

    const handleChange = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: typeof value === "string"
                ? (value.trim() === "" ? null : value)
                : (value ?? null),
        }));
    };

    const handleDaysChange = (e) => {
        const raw = e.target.value;
        if (raw === "") { handleChange("usance_days", null); return; }
        const v = parseInt(raw);
        if (!isNaN(v) && v >= 0) handleChange("usance_days", v);
    };

    // ── Handle SBLC selection — warn if isValid is false, but allow ──────────
    const handleSblcChange = (value) => {
        handleChange("sblc_id", value);
        if (value && value.isValid === false) {
            setShowSblcInvalidModal(true);
        }
    };

    const locked = (fieldName) =>
        Boolean(isPeriodLocked && isFieldLocked(fieldName, "Payment Terms", "Pre-Shipment", lockedFields));

    const LockAdornment = ({ fieldName }) =>
        locked(fieldName) ? (
            <InputAdornment position="end" sx={{ marginRight: "10px" }}>
                <Tooltip title="This field is locked and cannot be edited due to period lock." arrow placement="top">
                    <InfoIcon fontSize="small" sx={{ cursor: "default" }} />
                </Tooltip>
            </InputAdornment>
        ) : null;

    const numericKeyDown = (e) => {
        if (["e", "E", "+", "-", "ArrowUp", "ArrowDown"].includes(e.key)) e.preventDefault();
    };

    const handleApiError = (error) => {
        if (axios.isCancel(error) || error.code === "ERR_CANCELED") return;
        const status = error?.response?.status;
        if (status === 403) { triggerSessionExpire(); return; }
        setAlertMessage(
            status === 401 ? "Unauthorized access."
                : status === 429 ? "Request limit exceeded."
                    : "An error occurred. Please try again."
        );
        setAlertType("error");
        setShowAlert(true);
        if (status === 429) setTimeout(logOut, 3000);
    };

    const FromDateDropdown = ({ options }) => (
        <Grid size={{ xs: 6 }}>
            <VirtualizedAutocomplete
                isMultiple={false}
                isObject={true}
                isRequired={true}
                isWritable={isWritable}
                isLocked={locked("from_date_field")}
                filterOn="Calculate From"
                options={options}
                selectedOptions={formData.from_date_field || null}
                handleSelectChange={(_, v) => handleChange("from_date_field", v)}
            />
        </Grid>
    );

    // ── Custom SBLC render option ─────────────────────────────────────────────
    const renderSblcOption = (props, option) => {
        const isInvalid = option.isValid === false;
        const isNegative = option.adjustedLimit != null && option.adjustedLimit < 0;

        return (
            <Tooltip key={option.id} title={isInvalid ? `This SBLC's validity date has passed. You may still select it.` : ""} placement="right" arrow>
                <span style={{ display: "block" }}>
                    <li {...props} style={{ ...props.style }}>
                        {option.value}
                        {option.adjustedLimit != null && (
                            <span style={{ marginLeft: 8, fontSize: "0.75em", color: isNegative ? "#d32f2f" : "#2e7d32", fontWeight: 500 }}>
                                (Remaining: {option.adjustedLimit.toLocaleString()})
                            </span>
                        )}
                        {isInvalid && (
                            <span style={{ marginLeft: 6, fontSize: "0.75em", color: "#e65100" }}>
                                (Expired)
                            </span>
                        )}
                    </li>
                </span>
            </Tooltip>
        );
    };

    const buildPayload = (overridePfiId = null, overrideVendorId = undefined) => ({
        Business_Unit_Id: selectedBusinessUnit,
        deal_pfi_id: overridePfiId ?? selectedPFI,
        payment_term_leg: "Buying",
        payment_term_type_id: formData.payment_term_type_id,
        advance_amount: formData.advance_amount ?? null,
        advance_percentage: formData.advance_percentage ?? null,
        remaining_balance_percentage: autoRemainingPct !== "" ? parseFloat(autoRemainingPct) : null,
        tenor_type: formData.tenor_type ?? null,
        lc_type: formData.lc_type ?? null,
        usance_days: formData.usance_days ?? null,
        from_date_field: formData.from_date_field?.id ?? null,
        sblc_id: formData.sblc_id?.id ?? null,
        deal_value: buyingDealValue,
        vendor_id: overrideVendorId !== undefined
            ? overrideVendorId
            : (dealHeaderFormData?.Seller_Id?.id ?? null),
        user_id: currentUser?.id,
    });

    const isValid = () => {
        if (!formData.payment_term_type_id) return true;
        return formRef.current?.reportValidity() ?? true;
    };

    const handleSubmit = (pfiLockStatus) => {
        if (!formData.payment_term_type_id) return Promise.resolve(true);
        if (!isValid()) return Promise.resolve(false);

        return new Promise((resolve, reject) => {
            setShowAlert(false);
            setAlertMessage("");
            setAlertType("");

            const payload = buildPayload();
            if (isExisting) payload.id = formData.id;

            const url = isExisting ? "/api/UpdatePaymentTermDetailBuying" : "/api/AddPaymentTermDetailBuying";

            axios({ method: "post", url, data: payload, signal: abortController.signal })
                .then(({ status, data: respData }) => {
                    if (status === 200) {
                        setIsUpdated(false);
                        setInitialFormData(structuredClone(formData));
                        if (!isExisting && respData?.id) {
                            setFormData(prev => ({ ...prev, id: respData.id }));
                            setIsExisting(true);
                        }
                        resolve(true);
                    } else {
                        setAlertMessage(respData?.error || "An error occurred. Please try again.");
                        setAlertType("error");
                        setShowAlert(true);
                        reject(false);
                    }
                })
                .catch((error) => {
                    handleApiError(error);
                    reject(false);
                });
        });
    };

    useImperativeHandle(ref, () => ({
        isValid,
        handleSubmit,
        getPaymentTermTypeId: () => formData.payment_term_type_id,
        getSblcId: () => formData.sblc_id,
        handleReset: () => {
            setFormData(structuredClone(formDataSchema));
            setInitialFormData(structuredClone(formDataSchema));
            setIsExisting(false);
        },
        syncSblcLabel: (sblcId, newLabel) => {
            setFormData(prev => {
                const currentId = prev.sblc_id?.id ?? prev.sblc_id;
                if (currentId !== sblcId) return prev;
                return { ...prev, sblc_id: { ...prev.sblc_id, value: newLabel } };
            });
        },
    }));

    console.log(formData, 'formdata')

    return (

        <>
            <Accordion
                sx={{ marginRight: "0.1em !important" }}
                expanded={expandedFormSections.includes(6)}
                onChange={handleAccordionChange(6)}
            >
                <AccordionSummary
                    id="buying-payment-terms-header"
                    aria-controls="buying-payment-terms-content"
                    expandIcon={<ExpandMoreIcon />}
                    slot="div"
                >
                    <small className="page-heading title">Buying Payment Terms</small>
                </AccordionSummary>

                <AccordionDetails>
                    <form ref={formRef} className="deal-form mt-1" onSubmit={(e) => e.preventDefault()}>
                        <Grid container spacing={"10px"} className="align-items-start autocomplete">

                            <Grid size={{ xs: 6 }}>
                                <VirtualizedAutocomplete
                                    isLoading={isDetailLoading}
                                    isMultiple={false}
                                    isObject={true}
                                    isRequired={true}
                                    isWritable={isWritable}
                                    isLocked={locked("payment_term_type_id")}
                                    filterOn="Payment Terms"
                                    options={paymentTermTypes}
                                    useRawOptionLabels={true}
                                    selectedOptions={selectedType}
                                    handleSelectChange={(_, v) => {
                                        handleChange("payment_term_type_id", v?.id ?? null);
                                        resetSubFields();
                                    }}
                                />
                            </Grid>

                            {isMixedPayment && (
                                <>
                                    <Grid size={{ xs: 6 }} className="form-text-field">
                                        <TextField
                                            label="Advance %" type="number" required
                                            disabled={locked("advance_percentage")}
                                            value={formData.advance_percentage ?? ""}
                                            onChange={(e) => {
                                                const v = parseFloat(e.target.value);
                                                if (e.target.value === "" || (v >= 0 && v <= 100))
                                                    handleChange("advance_percentage", e.target.value === "" ? null : v);
                                            }}
                                            size="small"
                                            slotProps={{ input: { readOnly: !isWritable, endAdornment: <LockAdornment fieldName="advance_percentage" /> } }}
                                            onKeyDown={numericKeyDown}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 6 }} className="form-text-field">
                                        <TextField
                                            label="Remaining Balance % (AUTO)" type="number"
                                            value={autoRemainingPct} disabled size="small"
                                            slotProps={{ input: { readOnly: true } }}
                                        />
                                    </Grid>

                                    <Grid size={{ xs: 6 }}>
                                        <VirtualizedAutocomplete
                                            isMultiple={false} isObject={false} isWritable={isWritable}
                                            isLocked={locked("tenor_type")} filterOn="Tenor"
                                            options={mixedTenorOptions}
                                            selectedOptions={formData.tenor_type || null}
                                            handleSelectChange={(_, v) => {
                                                handleChange("tenor_type", v);
                                                setFormData(prev => ({ ...prev, lc_type: null, usance_days: null, from_date_field: null }));
                                            }}
                                        />
                                    </Grid>

                                    {tenorType === "LC" && (
                                        <>
                                            <Grid size={{ xs: 6 }}>
                                                <VirtualizedAutocomplete
                                                    isMultiple={false} isObject={false} isWritable={isWritable}
                                                    isRequired={true}
                                                    isLocked={locked("lc_type")} filterOn="LC Tenor"
                                                    options={mixedLCTypeOptions}
                                                    selectedOptions={formData.lc_type || null}
                                                    handleSelectChange={(_, v) => {
                                                        handleChange("lc_type", v);
                                                        setFormData(prev => ({ ...prev, usance_days: null, from_date_field: null }));
                                                    }}
                                                />
                                            </Grid>

                                            {lcType === "USANCE" && (
                                                <>
                                                    <Grid size={{ xs: 6 }} className="form-text-field">
                                                        <TextField
                                                            label="Usance Days" type="number" required
                                                            disabled={locked("usance_days")} value={formData.usance_days ?? ""}
                                                            onChange={handleDaysChange} size="small"
                                                            slotProps={{ input: { readOnly: !isWritable, inputProps: { min: 0 }, endAdornment: <LockAdornment fieldName="usance_days" /> } }}
                                                            onKeyDown={numericKeyDown}
                                                        />
                                                    </Grid>
                                                    <FromDateDropdown options={fromDateOptionsNoCopyDocs} />
                                                </>
                                            )}
                                        </>
                                    )}

                                    {tenorType === "OPEN ACCOUNT" && (
                                        <>
                                            <Grid size={{ xs: 6 }} className="form-text-field">
                                                <TextField
                                                    label="Days for Remaining Balance" type="number" required
                                                    disabled={locked("usance_days")} value={formData.usance_days ?? ""}
                                                    onChange={handleDaysChange} size="small"
                                                    slotProps={{ input: { readOnly: !isWritable, inputProps: { min: 0 }, endAdornment: <LockAdornment fieldName="usance_days" /> } }}
                                                    onKeyDown={numericKeyDown}
                                                />
                                            </Grid>
                                            <FromDateDropdown options={fromDateOptionsWithCopyDocs} />
                                        </>
                                    )}
                                </>
                            )}

                            {isLC && (
                                <>
                                    <Grid size={{ xs: 6 }}>
                                        <VirtualizedAutocomplete
                                            isMultiple={false} isObject={false} isWritable={isWritable}
                                            isLocked={locked("tenor_type")} filterOn="Tenor"
                                            options={lcTenorOptions}
                                            selectedOptions={formData.tenor_type || null}
                                            handleSelectChange={(_, v) => {
                                                handleChange("tenor_type", v);
                                                setFormData(prev => ({
                                                    ...prev,
                                                    lc_type: null, usance_days: null,
                                                    advance_percentage: null, remaining_balance_percentage: null,
                                                    from_date_field: null,
                                                }));
                                            }}
                                        />
                                    </Grid>

                                    {tenorType === "USANCE" && (
                                        <>
                                            <Grid size={{ xs: 6 }} className="form-text-field">
                                                <TextField
                                                    label="Usance Days" type="number" required
                                                    disabled={locked("usance_days")} value={formData.usance_days ?? ""}
                                                    onChange={handleDaysChange} size="small"
                                                    slotProps={{ input: { readOnly: !isWritable, inputProps: { min: 0 }, endAdornment: <LockAdornment fieldName="usance_days" /> } }}
                                                    onKeyDown={numericKeyDown}
                                                />
                                            </Grid>
                                            <FromDateDropdown options={fromDateOptionsNoCopyDocs} />
                                        </>
                                    )}

                                    {tenorType === "MIXED" && (
                                        <>
                                            <Grid size={{ xs: 6 }} className="form-text-field">
                                                <TextField
                                                    label="Sight %" type="number" required
                                                    disabled={locked("advance_percentage")} value={formData.advance_percentage ?? ""}
                                                    onChange={(e) => {
                                                        const v = parseFloat(e.target.value);
                                                        if (e.target.value === "" || (v >= 0 && v <= 100))
                                                            handleChange("advance_percentage", e.target.value === "" ? null : v);
                                                    }}
                                                    size="small"
                                                    slotProps={{ input: { readOnly: !isWritable, endAdornment: <LockAdornment fieldName="advance_percentage" /> } }}
                                                    onKeyDown={numericKeyDown}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 6 }} className="form-text-field">
                                                <TextField
                                                    label="Usance % (AUTO)" type="number"
                                                    value={autoRemainingPct} disabled size="small"
                                                    slotProps={{ input: { readOnly: true } }}
                                                />
                                            </Grid>
                                            <Grid size={{ xs: 6 }} className="form-text-field">
                                                <TextField
                                                    label="Remaining Balance Usance Days" type="number" required
                                                    disabled={locked("usance_days")} value={formData.usance_days ?? ""}
                                                    onChange={handleDaysChange} size="small"
                                                    slotProps={{ input: { readOnly: !isWritable, inputProps: { min: 0 }, endAdornment: <LockAdornment fieldName="usance_days" /> } }}
                                                    onKeyDown={numericKeyDown}
                                                />
                                            </Grid>
                                            <FromDateDropdown options={fromDateOptionsNoCopyDocs} />
                                        </>
                                    )}
                                </>
                            )}

                            {isSBLC && (
                                <Grid size={{ xs: 6 }}>
                                    <VirtualizedAutocomplete
                                        isLoading={isSblcListLoading}
                                        isMultiple={false}
                                        isObject={true}
                                        isWritable={isWritable}
                                        isLocked={locked("sblc_id")}
                                        filterOn="Select Buying SBLC"
                                        options={sblcOptionsWithDisabled}
                                        selectedOptions={formData.sblc_id || null}
                                        handleSelectChange={(_, v) => handleSblcChange(v)}
                                        renderOption={renderSblcOption}
                                    />
                                </Grid>
                            )}

                            {isOpenAccount && (
                                <>
                                    <Grid size={{ xs: 6 }} className="form-text-field">
                                        <TextField
                                            label="Days for Payment" type="number" required
                                            disabled={locked("usance_days")} value={formData.usance_days ?? ""}
                                            onChange={handleDaysChange} size="small"
                                            slotProps={{ input: { readOnly: !isWritable, inputProps: { min: 0 }, endAdornment: <LockAdornment fieldName="usance_days" /> } }}
                                            onKeyDown={numericKeyDown}
                                        />
                                    </Grid>
                                    <FromDateDropdown options={fromDateOptionsWithCopyDocs} />
                                </>
                            )}

                            {isCollection && (
                                <>
                                    <Grid size={{ xs: 6 }}>
                                        <VirtualizedAutocomplete
                                            isMultiple={false} isObject={false} isWritable={isWritable}
                                            isLocked={locked("tenor_type")} filterOn="Tenor"
                                            options={collectionTypeOptions}
                                            selectedOptions={formData.tenor_type || null}
                                            handleSelectChange={(_, v) => {
                                                handleChange("tenor_type", v);
                                                setFormData(prev => ({ ...prev, usance_days: null, from_date_field: null }));
                                            }}
                                        />
                                    </Grid>

                                    {tenorType === "D/A" && (
                                        <>
                                            <Grid size={{ xs: 6 }} className="form-text-field">
                                                <TextField
                                                    label="Acceptance Days" type="number" required
                                                    disabled={locked("usance_days")} value={formData.usance_days ?? ""}
                                                    onChange={handleDaysChange} size="small"
                                                    slotProps={{ input: { readOnly: !isWritable, inputProps: { min: 0 }, endAdornment: <LockAdornment fieldName="usance_days" /> } }}
                                                    onKeyDown={numericKeyDown}
                                                />
                                            </Grid>
                                            <FromDateDropdown options={fromDateOptionsNoCopyDocs} />
                                        </>
                                    )}
                                </>
                            )}

                        </Grid>
                    </form>
                </AccordionDetails>
            </Accordion>

            {/* ── Deal value exceeds SBLC limit warning (no deselect) ──────────── */}
            <Dialog open={showSblcWarningModal} onClose={() => setShowSblcWarningModal(false)}>
                <DialogContent>
                    <DialogContentText className="text-center">
                        {sblcWarningMessage}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        type="button"
                        className="btn btn-primary"
                        variant="contained"
                        style={{ width: "fit-content !important" }}
                        onClick={() => setShowSblcWarningModal(false)}>
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>

            {/* ── SBLC validity expired warning (selection still allowed) ─────── */}
            <Dialog open={showSblcInvalidModal} onClose={() => setShowSblcInvalidModal(false)}>
                <DialogContent>
                    <DialogContentText className="text-center">
                        The validity date for the selected SBLC has passed. It is still allowed to be used, but please verify with your team before proceeding.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        type="button"
                        className="btn btn-primary"
                        variant="contained"
                        style={{ width: "fit-content !important" }}
                        onClick={() => setShowSblcInvalidModal(false)}>
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
});

export default memo(BuyingPaymentTerms);